import instance from './instance';

export enum VerificationWebsiteStatus {
  Pending = 'pending',
  Rejected = 'rejected',
  Verified = 'verified',
  Deleted = 'deleted',
  Expired = 'expired',
}

export interface Website {
  id: number;
  base: string;
  api_key: string;
  category: string[];
  post_date: string;
  last_update: string;
  title: string | undefined;
  description: string | undefined;
  show_only_imported: boolean;
}

interface EmailVerificationCode {
  id: number;
  code: string;
  email: string;
  created: string;
  createdDate: string;
}

export interface TVerificationWebsite {
  id: number;
  domain: string;
  status: VerificationWebsiteStatus;
  created: string;
  updated: string;
  dns_code: string;
  whois_email: string;
  site: Website | null;
  email_verification_codes: EmailVerificationCode[];
  dns_records_updated: string;
  errors_count: number;
  description: string;
  allow_delete: boolean;
  assemble_data_status: 'in_progress' | 'completed';
  assembled: string | null;
}

export const getUsersWebsites = () =>
  instance.get<{items: TVerificationWebsite[]}>('/frontend/api/v1/import-api/get-user-sites');

export const getWebsiteById = (id: number) =>
  instance.get<TVerificationWebsite>(`/frontend/api/v1/import-api/get-user-sites/${id}`);

export const assignWebsite = (domain: string) =>
  instance.post<{status: string; site_verification: TVerificationWebsite}>(
    '/frontend/api/v1/import-api/assign-site',
    {domain}
  );

export const getApiKey = (siteId: number) =>
  instance.get<{key: string}>(`/frontend/api/v1/import-api/get-api-key`, {
    params: {
      site_id: siteId,
    },
  });

export interface TDNSResponse {
  records: string[];
}

export interface ModelProfile {
  id: string;
  hotUrl: string | null;
  created_at: string;
  updated_at: string;
  name: string;
  phone: string | null;
  thumb: string;
  url: string;
  profile_id: string;
  external_id: string;
  type: 'prorvider' | 'webcam model';
}

export const verifyWebsiteByDns = (id: number) =>
  instance.post<TVerificationWebsite>(
    `/frontend/api/v1/import-api/verification/${id}/verify-by-dns`
  );

export const verifyWebsiteByEmail = (siteVerificationId: number) =>
  instance.post<TVerificationWebsite>(
    `/frontend/api/v1/import-api/verification/${siteVerificationId}/verify-by-email`
  );

export const getWebsiteDnsTextRecords = (id: number) =>
  instance.get<TDNSResponse>(`/frontend/api/v1/import-api/verification/${id}/dns-txt-records`);

export const getWebsiteApiKey = (siteId: number) =>
  instance.get<{key: string}>('/frontend/api/v1/import-api/get-api-key', {
    params: {
      site_id: siteId,
    },
  });

export const regenerateApiKey = (siteId: number) =>
  instance.post<{key: string}>('/frontend/api/v1/import-api/regenerate-api-key', {site_id: siteId});

export const getProfilesBySiteId = (siteId: number, offset?: number) =>
  instance.post<{
    items: ModelProfile[];
    total: number;
  }>('/frontend/api/v1/import-api/get-profiles', {site_id: siteId, offset});

export const unsassignWebsite = (siteVerificationId: number) =>
  instance.post<void>(`/frontend/api/v1/import-api/unassign-site/${siteVerificationId}`);

export interface HistoryItem {
  id: string;
  created: string;
  created_models: number;
  deleted_models: number;
  updated_models: number;
  duration: number;
  status: 'completed';
  ip: string;
  logs_count: number;
}

interface GetHistoryResponse {
  total: number;
  items: HistoryItem[];
}

export const getImportHistory = ({siteId, offset, limit}: any) =>
  instance.get<GetHistoryResponse>(`/frontend/api/v1/import-api/sites/${siteId}/import-history`, {
    params: {
      offset,
      limit,
    },
  });

export interface LogItem {
  id: 'string';
  created: '2024-05-02T14:24:00.963Z';
  severity: 'INFO';
  message: 'string';
}

interface GetHistoryLogsResponse {
  total: number;
  items: LogItem[];
  entity: HistoryItem;
}

export const getImportHistoryLogs = ({siteId, importHistoryId, offset, limit}: any) =>
  instance.get<GetHistoryLogsResponse>(
    `/frontend/api/v1/import-api/sites/${siteId}/import-history/${importHistoryId}/log-records`,
    {
      params: {
        offset,
        limit,
      },
    }
  );

export const reopenWebsite = (siteVerificationId: number) =>
  instance.post<void>(`/frontend/api/v1/import-api/verification/${siteVerificationId}/reopen`);

type UpdateWebsiteBody = Pick<Website, 'show_only_imported'>;

export const updateSite = (siteId: number | string, data: Partial<UpdateWebsiteBody>) =>
  instance.put<void>(`/frontend/api/v1/import-api/sites/${siteId}`, data);
